import { useState, useEffect } from 'react';
import { doc, setDoc, deleteDoc } from 'firebase/firestore';
import { db } from '../firebaseConfig';
import './Editor.css';

export const BroadcasterCreator = ({ onSave, onCancel, initialData, onDelete }) => {
    const isEditing = !!initialData;

    const [formData, setFormData] = useState({
        broadcaster_name: '',
        broadcaster_id: '',
        broadcaster_type: 'SMAD par abonnement',
        broadcaster_hq_abroad: false,
    });
    const [isSubmitting, setIsSubmitting] = useState(false);

    // Populate form if editing
    useEffect(() => {
        if (initialData) {
            setFormData({
                broadcaster_name: initialData.broadcaster_name || '',
                broadcaster_id: initialData.broadcaster_id || '',
                broadcaster_type: initialData.broadcaster_type || 'SMAD par abonnement',
                broadcaster_hq_abroad: initialData.broadcaster_hq_abroad || false,
            });
        } else {
            // Reset if switching to create mode
            setFormData({
                broadcaster_name: '',
                broadcaster_id: '',
                broadcaster_type: 'SMAD par abonnement',
                broadcaster_hq_abroad: false,
            });
        }
    }, [initialData]);

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const handleNameBlur = () => {
        // Only auto-generate ID in creation mode
        if (!isEditing && !formData.broadcaster_id && formData.broadcaster_name) {
            const slug = formData.broadcaster_name
                .toLowerCase()
                .replace(/[^a-z0-9]+/g, '')
                .trim();
            setFormData(prev => ({ ...prev, broadcaster_id: slug }));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);

        try {
            if (!formData.broadcaster_id || !formData.broadcaster_name) {
                throw new Error("Le nom et l'ID sont obligatoires.");
            }

            // Merge existing data if editing (to preserve rules), otherwise set defaults
            const baseData = isEditing ? initialData : {
                broadcaster_minCA: null,
                rules: {
                    broadcastObligations: [],
                    chronoWindows: [],
                    financingObligations: []
                },
                broadcaster_comment: null,
                regulation_texts: []
            };

            const finalData = {
                ...baseData,
                ...formData,
                firestoreId: formData.broadcaster_id
            };

            // Use setDoc (merge is true implies we don't overwrite blindly, 
            // but here we constructed the full object so standard setDoc is fine. 
            // For safety we can use { merge: true })
            await setDoc(doc(db, "broadcasters", finalData.firestoreId), finalData, { merge: true });

            onSave(finalData);
        } catch (error) {
            console.error("Error saving broadcaster:", error);
            alert("Erreur: " + error.message);
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleDelete = async () => {
        if (!isEditing || !formData.broadcaster_id) return;
        
        if (window.confirm(`ATTENTION : Vous êtes sur le point de supprimer définitivement "${formData.broadcaster_name}".\n\nCette action est irréversible.`)) {
            setIsSubmitting(true);
            try {
                await deleteDoc(doc(db, "broadcasters", formData.broadcaster_id));
                if (onDelete) onDelete(formData.broadcaster_id);
            } catch (error) {
                console.error("Error deleting broadcaster:", error);
                alert("Erreur lors de la suppression: " + error.message);
                setIsSubmitting(false);
            }
        }
    };

    return (
        <div className="rule-editor">
            <h3 style={{ borderBottom: '2px solid #eee', paddingBottom: '10px', marginBottom: '20px' }}>
                {isEditing ? 'Modifier le Diffuseur' : 'Nouveau Diffuseur'}
            </h3>
            
            <form onSubmit={handleSubmit}>
                <div className="form-group">
                    <label htmlFor="bc-name">Nom du diffuseur</label>
                    <input 
                        type="text" 
                        name="broadcaster_name" 
                        id="bc-name" 
                        value={formData.broadcaster_name} 
                        onChange={handleChange} 
                        onBlur={handleNameBlur}
                        required 
                    />
                </div>

                <div className="form-group">
                    <label htmlFor="bc-id">Identifiant unique (ID)</label>
                    <input 
                        type="text" 
                        name="broadcaster_id" 
                        id="bc-id" 
                        value={formData.broadcaster_id} 
                        onChange={handleChange} 
                        disabled={isEditing} // ID cannot be changed once created
                        style={isEditing ? { backgroundColor: '#f0f0f0', cursor: 'not-allowed' } : {}}
                        required 
                    />
                    {!isEditing && <small style={{color: '#666'}}>Utilisé comme ID Firestore.</small>}
                </div>

                <div className="form-group">
                    <label htmlFor="bc-type">Type</label>
                    <select 
                        name="broadcaster_type" 
                        id="bc-type" 
                        value={formData.broadcaster_type} 
                        onChange={handleChange}
                    >
                        <option value="SMAD par abonnement">SMAD par abonnement</option>
                        <option value="SMAD gratuit">SMAD gratuit</option>
                        <option value="Chaîne de télévision">Chaîne de télévision</option>
                        <option value="Cinéma">Cinéma</option>
                        <option value="Autre">Autre</option>
                    </select>
                </div>

                <div className="form-group" style={{flexDirection: 'row', alignItems: 'center', gap: '10px'}}>
                    <input 
                        type="checkbox" 
                        name="broadcaster_hq_abroad" 
                        id="bc-hq" 
                        checked={formData.broadcaster_hq_abroad} 
                        onChange={handleChange}
                        style={{width: 'auto'}}
                    />
                    <label htmlFor="bc-hq" style={{marginBottom: 0}}>Siège social à l'étranger ?</label>
                </div>

                <div className="form-actions" style={{ display: 'flex', gap: '10px', marginTop: '20px' }}>
                    <button type="submit" className="save-btn" disabled={isSubmitting}>
                        {isSubmitting ? 'Enregistrement...' : 'Enregistrer'}
                    </button>
                    <button type="button" onClick={onCancel} className="cancel-btn">
                        Fermer
                    </button>
                    
                    {isEditing && (
                        <button 
                            type="button" 
                            onClick={handleDelete} 
                            className="delete-btn" 
                            disabled={isSubmitting}
                            style={{ marginLeft: 'auto', backgroundColor: '#dc3545', color: 'white' }}
                        >
                            Supprimer
                        </button>
                    )}
                </div>
            </form>
        </div>
    );
};